import { useContext } from "react";
import { LayoutStateContext } from "../../../contexts/layout_state";
import { useCssStateManager } from "../../../hooks/use_css_state_manager";
import { useCustomRouter } from "../../../hooks/use_custom_router";
import { useSmartState } from "../../../hooks/use_smart_state";
import { prebuildData } from "../../../lib/prebuild-data";
import { ConfigCollectionData } from "../../../lib/prebuild-data/types";
import { EventPane } from "../../core/event_pane";
import { Icon } from "../../core/icon";
import CollectionDropdownMenu from "./collection_dropdown_menu";

export type CollectionNavBarProps = {
    leftIconName: string;
    iconSize?: number;
    rightIconName?: string;
    leftIconClickHandler?: () => void;
    rightIconClickHandler?: () => void;
    leftIconSeoLinkUrl?: string;
    collectionItem: ConfigCollectionData;
    collectionItems: {handle: string;caption: string}[];
};

export default function CollectionNavBar({
        leftIconName,
        iconSize = 30,
        rightIconName,
        leftIconSeoLinkUrl,
        leftIconClickHandler = () => {},
        rightIconClickHandler = () => {},
        collectionItem,
        collectionItems,
    }:CollectionNavBarProps)
{
    const cssStateManager = useCssStateManager(["collection-nav-bar"]);

    const dropdownMenuOpen = useSmartState(false);
    const { page,collectionSearchBarOpen,mobileDetect: { miniWidthCutoff } } = useContext(LayoutStateContext);
    const router = useCustomRouter();

    cssStateManager.useProperty(collectionSearchBarOpen.value,"search-bar-open");
    cssStateManager.useProperty(!!rightIconName,"has-right-icon");

    const collectionDropdownClickHandler = () => {
        dropdownMenuOpen.value = true;
        page.hideOverlayScrollbar();
    };

    const collectionDropdownCloseClickHandler = () => {
        dropdownMenuOpen.value = false;
        page.showOverlayScrollbar();
    };

    const collectionDropdownItemClickHandler = (handle: string) => {
        collectionDropdownCloseClickHandler();
        const collectionItem = prebuildData.getCollectionByHandle(handle);
        if (collectionItem)
        {
            const route = collectionItem.route.split("{handle}").join(handle);
            router.push(route);
        }            
    };

    return (
        <>
            <div className={cssStateManager.getClassNames()}>
                <div className="left-open-button">
                    <Icon name={leftIconName} size={iconSize} />
                    <EventPane onClick={leftIconClickHandler} {...(leftIconSeoLinkUrl?{seoLinkUrl: leftIconSeoLinkUrl}:{})} />
                </div>
                <div className="collection-title-dropdown">
                    {collectionItem.caption}
                    <div className="arrow-icon">
                        <Icon name="navbar-right-arrow" size={18} />
                    </div>
                    <EventPane onClick={collectionDropdownClickHandler} />
                </div>
                {rightIconName?
                    (
                        <div className="right-open-button">
                            <Icon name={rightIconName} size={iconSize} />
                            <EventPane onClick={rightIconClickHandler} />
                        </div>
                    ):""}                
            </div>
            <CollectionDropdownMenu
                open={dropdownMenuOpen.value}
                collectionHandle={collectionItem.seo.handle}
                collectionItems={collectionItems}
                collectionDropdownCloseClickHandler={collectionDropdownCloseClickHandler}
                collectionDropdownItemClickHandler={collectionDropdownItemClickHandler}
            />
            <style jsx>{`
            .collection-nav-bar {
                display: flex;
                align-items: center;         
                --duration: var(--search-open-duration);
            }

            .left-open-button,.collection-title-dropdown {
                opacity: 1;
                visibility: visible;
            }

            .search-bar-open .left-open-button ,.search-bar-open .collection-title-dropdown {
                opacity: 0;
                visibility: hidden;
            }

            .left-open-button {
                padding: 5rem 12rem;
                position: relative;
                transition: background-color 0.2s,color 0.3s,opacity var(--duration),visibility var(--duration);
                color: #333;
            }

            .right-open-button {
                padding: 5rem 12rem;
                position: relative;
                transition: background-color 0.2s,color 0.3s;
                color: #333;
            }

            .left-open-button.touchdown,.collection-title-dropdown.touchdown,.right-open-button.touchdown {
                background-color: #eaeaea;
            }

            .left-open-button.hover {
                color: #999;
            }

            .collection-title-dropdown.hover {
                background-color: #efefef;
            }

            .collection-title-dropdown {
                border: 2rem solid #444444;
                flex: 1 0;
                text-align: center;
                display: flex;
                flex-direction: column;
                justify-content: center;                
                border-radius: 5rem;
                margin: 0rem 4rem;
                height: 41rem;                
                position: relative;
                padding-right: 20rem;
                transition: background-color 0.3s,opacity var(--duration),visibility var(--duration);
            }

            .collection-title-dropdown .arrow-icon {
                position: absolute;
                right: 12rem;
                top: 8rem;
                transform: rotate(90deg) scaleX(0.7);
                color: #999;
            }

            @media screen and (max-width: ${miniWidthCutoff}px) {
                .has-right-icon .collection-title-dropdown {
                    font-size: 13rem;   
                }
            }

            `}</style>
        </>
    );
}